import {
  Payout,
  PayoutIndex,
  PayoutScreenMessage,
  PayoutShowView,
  PayoutTroubleshoot
} from "../../Contracts/Transaction";
import { create } from "zustand";
import { useRequestStore } from "../BackendStore";
import { PaymentsIntegrationEnum } from "../../Contracts/Integration";

const payoutsUrl = "/v1/payments/payouts";
const payoutsDetailsUrl = "/v1/payments/payouts/{payout_id}/show";
const payoutTroubleshootUrl = "/v1/payments/payouts/{payout_id}/troubleShoot";
const payoutReconcileUrl = "/v1/payments/payouts/{payout_id}/reconcile";
const adminPayoutTroubleshootUrl =
  "/v1/payments/admin/teams/{team_id}/payout/troubleShoot/{payout_id}";
const adminPayoutListUrl = "/v1/payments/admin/teams/payments/payout/{team_id}";

interface PayoutState {
  isLoading: boolean;
  payouts: Payout[];
  payoutsCount?: number;
  availableBalance?: number;
  hasPayoutCapability?: boolean;
  isReviewingKYC?: boolean;
  payoutScreenMessage?: PayoutScreenMessage;
  connectedIntegration?: PaymentsIntegrationEnum;
  getPayouts: (
    getAccessTokenSilently: any,
    limit?: number,
    sort?: string,
    page?: number
  ) => Promise<void>;
  payoutDetails?: PayoutShowView;
  getPayoutDetails: (getAccessTokenSilently: any, payoutId: string) => Promise<void>;
  payoutTroubleshoot?: PayoutTroubleshoot;
  adminPayoutTroubleshoot?: PayoutTroubleshoot;
  getPayoutTroubleshoot: (getAccessTokenSilently: any, payoutId: string) => Promise<void>;
  getAdminPayoutTroubleshoot: (
    getAccessTokenSilently: any,
    payoutId: string,
    teamId: string
  ) => Promise<void>;
  getPayoutReconcile: (
    getAccessTokenSilently: any,
    payoutId: number
  ) => Promise<{ message: string; success: boolean }>;
  getAdminPayoutList: (
    getAccessTokenSilently: any,
    teamId: string,
    limit?: number,
    sort?: string,
    page?: number
  ) => Promise<void>;
}

export const usePayoutStore = create<PayoutState>((set, getState) => {
  const { getRequest } = useRequestStore.getState();

  const getPayouts = async (
    getAccessTokenSilently: any,
    limit?: number,
    sort?: String,
    page?: number
  ) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<PayoutIndex>(getAccessTokenSilently, payoutsUrl, {
        limit,
        sort,
        page,
        include_total_count: true
      });
      set({
        payouts: response.payments_payouts.data,
        payoutsCount: response.payments_payouts.filtered_count_map?.all,
        availableBalance: response.available_balance,
        hasPayoutCapability: response.has_payout_capability,
        isReviewingKYC: response.is_reviewing_kyc,
        payoutScreenMessage: response.payout_screen_message,
        isLoading: false
      });
    } catch (error) {
      console.error("Error fetching payouts", error);
      set({ isLoading: false });
    }
  };

  const getPayoutDetails = async (getAccessTokenSilently: any, payoutId: string) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<PayoutShowView>(
        getAccessTokenSilently,
        payoutsDetailsUrl.replace("{payout_id}", payoutId)
      );
      set({ payoutDetails: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching payout details", error);
      set({ isLoading: false });
    }
  };

  const getPayoutTroubleshoot = async (getAccessTokenSilently: any, payoutId: string) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<PayoutTroubleshoot>(
        getAccessTokenSilently,
        payoutTroubleshootUrl.replace("{payout_id}", payoutId)
      );
      set({ payoutTroubleshoot: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching payout troubleshoot", error);
      set({ isLoading: false });
    }
  };

  const getPayoutReconcile = async (getAccessTokenSilently: any, payoutId: number) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<string>(
        getAccessTokenSilently,
        payoutReconcileUrl.replace("{payout_id}", payoutId.toString())
      );
      set({ isLoading: false });
      return { message: response, success: true };
    } catch (error) {
      console.error("Error reconciling payout", error);
      set({ isLoading: false });
      if (error.response) {
        return {
          message: error.response.data.message as string,
          success: false
        };
      } else {
        const existingErrorMessage = getState().payouts.find(
          p => p.id === payoutId
        )?.payout_reconcile_display_error;
        return {
          message: existingErrorMessage || "Error reconciling payout",
          success: false
        };
      }
    }
  };

  const getAdminPayoutTroubleshoot = async (
    getAccessTokenSilently: any,
    payoutId: string,
    teamId: string
  ) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<PayoutTroubleshoot>(
        getAccessTokenSilently,
        adminPayoutTroubleshootUrl.replace("{payout_id}", payoutId).replace("{team_id}", teamId)
      );
      set({ adminPayoutTroubleshoot: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching admin payout troubleshoot", error);
      set({ isLoading: false });
    }
  };

  const getAdminPayoutList = async (
    getAccessTokenSilently: any,
    teamId: string,
    limit?: number,
    sort?: string,
    page?: number
  ) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<PayoutIndex>(
        getAccessTokenSilently,
        adminPayoutListUrl.replace("{team_id}", teamId),
        {
          limit,
          sort,
          page,
          include_total_count: true
        }
      );
      set({
        payouts: response.payments_payouts.data,
        payoutsCount: response.payments_payouts.filtered_count_map?.all,
        availableBalance: response.available_balance,
        hasPayoutCapability: response.has_payout_capability,
        isReviewingKYC: response.is_reviewing_kyc,
        payoutScreenMessage: response.payout_screen_message,
        connectedIntegration: response.current_integration?.integration,
        isLoading: false
      });
    } catch (error) {
      console.error("Error fetching admin payout list", error);
      set({ isLoading: false });
    }
  };

  return {
    payouts: [],
    getPayouts,
    isLoading: true,
    getPayoutDetails,
    getPayoutTroubleshoot,
    getPayoutReconcile,
    getAdminPayoutTroubleshoot,
    getAdminPayoutList
  };
});
